import React, { useState, useEffect } from 'react';

/**
 * KnowledgeBaseFiltersSelector Component
 * 
 * A reusable component for selecting knowledge base metadata filters.
 * Supports manual multi-select dropdowns and LLM-assisted filter suggestion.
 * 
 * Props:
 * - filters: Current filter values { doc_types: [], sub_types: [], account_numbers: [] }
 * - onFiltersChange: Callback when filters change
 * - availableValues: Available options { doc_types: [], sub_types: [], account_numbers: [] }
 * - promptText: The prompt text to analyze for LLM suggestions
 * - onSuggestFilters: Callback to trigger LLM filter suggestion
 * - loading: Whether LLM suggestion is in progress
 * - disabled: Whether the component is disabled
 */
const KnowledgeBaseFiltersSelector = ({
  filters = { doc_types: [], sub_types: [] },
  onFiltersChange,
  availableValues = { doc_types: [], sub_types: [] },
  promptText = '',
  onSuggestFilters,
  loading = false,
  disabled = false,
  defaultExpanded = false
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [suggestionReasoning, setSuggestionReasoning] = useState('');

  // Check if any filters are set
  const hasFilters = (filters.doc_types?.length > 0) || 
                     (filters.sub_types?.length > 0);

  const handleFilterChange = (filterType, selectedValues) => {
    const newFilters = {
      ...filters,
      [filterType]: selectedValues
    };
    onFiltersChange(newFilters);
  };

  const handleToggleValue = (filterType, value) => {
    const currentValues = filters[filterType] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    handleFilterChange(filterType, newValues);
  };

  const handleSelectAll = (filterType) => {
    const allValues = availableValues[filterType] || [];
    handleFilterChange(filterType, [...allValues]);
  };

  const handleClearFilter = (filterType) => {
    handleFilterChange(filterType, []);
  };

  const handleClearAll = () => {
    onFiltersChange({ doc_types: [], sub_types: [] });
    setSuggestionReasoning('');
  };

  const handleSuggestClick = async () => {
    if (onSuggestFilters) {
      const result = await onSuggestFilters();
      if (result?.reasoning) {
        setSuggestionReasoning(result.reasoning);
      }
    }
  };

  const renderMultiSelect = (label, filterType, icon) => {
    const values = availableValues[filterType] || [];
    const selectedValues = filters[filterType] || [];

    if (values.length === 0) {
      return (
        <div className="mb-3">
          <label className="form-label d-flex align-items-center">
            <i className={`bi ${icon} me-2`}></i>
            {label}
          </label>
          <div className="text-muted small">
            <i className="bi bi-info-circle me-1"></i>
            No {label.toLowerCase()} available
          </div>
        </div>
      );
    }

    return (
      <div className="mb-3">
        <label className="form-label d-flex align-items-center justify-content-between">
          <span>
            <i className={`bi ${icon} me-2`}></i>
            {label}
            {selectedValues.length > 0 && (
              <span className="badge bg-primary ms-2">{selectedValues.length}</span>
            )}
          </span>
          {selectedValues.length > 0 && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => handleClearFilter(filterType)}
              disabled={disabled}
              title="Clear selection"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </label>
        <div 
          className="border rounded p-2" 
          style={{ 
            maxHeight: '150px', 
            overflowY: 'auto',
            backgroundColor: disabled ? '#f8f9fa' : 'white'
          }}
        >
          {values.map((value) => (
            <div 
              key={value} 
              className="form-check"
            >
              <input
                type="checkbox"
                className="form-check-input"
                id={`${filterType}-${value}`}
                checked={selectedValues.includes(value)}
                onChange={() => handleToggleValue(filterType, value)}
                disabled={disabled}
              />
              <label 
                className="form-check-label" 
                htmlFor={`${filterType}-${value}`}
                style={{ cursor: disabled ? 'default' : 'pointer' }}
              >
                {value}
              </label>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const hasAvailableValues = availableValues.doc_types?.length > 0 || 
                              availableValues.sub_types?.length > 0;

  return (
    <div className="knowledge-base-filters-selector">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <button
          type="button"
          className="btn btn-link text-decoration-none p-0 d-flex align-items-center"
          onClick={() => setIsExpanded(!isExpanded)}
          disabled={disabled}
        >
          <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} me-2`}></i>
          <i className="bi bi-funnel me-2"></i>
          <span className="fw-semibold">Knowledge Base Filters</span>
          {hasFilters && (
            <span className="badge bg-success ms-2">Active</span>
          )}
        </button>
        
        {isExpanded && hasFilters && (
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            onClick={handleClearAll}
            disabled={disabled}
          >
            <i className="bi bi-trash me-1"></i>
            Clear All
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="ps-4 border-start">
          {!hasAvailableValues ? (
            <div className="alert alert-info py-2">
              <i className="bi bi-info-circle me-2"></i>
              No metadata values available. Upload files with doc_type or sub_type metadata to enable filtering.
            </div>
          ) : (
            <>
              {/* LLM Suggestion Button - always show if onSuggestFilters is provided */}
              {onSuggestFilters && (
                <div className="mb-3">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm w-100"
                    onClick={handleSuggestClick}
                    disabled={disabled || loading || !promptText?.trim()}
                    title={!promptText?.trim() ? 'Select a prompt first to enable auto-suggest' : 'Analyze prompt and suggest filters'}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Analyzing prompt...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-magic me-2"></i>
                        Auto-suggest filters based on prompt
                      </>
                    )}
                  </button>
                  {suggestionReasoning && (
                    <div className="alert alert-info mt-2 py-2 small">
                      <i className="bi bi-lightbulb me-1"></i>
                      <strong>AI Reasoning:</strong> {suggestionReasoning}
                    </div>
                  )}
                </div>
              )}

              {/* Filter Multi-selects - side by side */}
              <div className="row">
                <div className="col-md-6">
                  {renderMultiSelect('Document Types', 'doc_types', 'bi-file-earmark-text')}
                </div>
                <div className="col-md-6">
                  {renderMultiSelect('Sub-Types', 'sub_types', 'bi-tag')}
                </div>
              </div>

              {/* Summary */}
              {hasFilters && (
                <div className="alert alert-secondary py-2 small mb-0">
                  <i className="bi bi-filter me-1"></i>
                  <strong>Active Filters:</strong>
                  <ul className="mb-0 ps-3 mt-1">
                    {filters.doc_types?.length > 0 && (
                      <li>Document Types: {filters.doc_types.join(', ')}</li>
                    )}
                    {filters.sub_types?.length > 0 && (
                      <li>Sub-Types: {filters.sub_types.join(', ')}</li>
                    )}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseFiltersSelector;
